import React from 'react';
import PropTypes from 'prop-types';
import { ErrorBoundary } from 'react-error-boundary';
import ErrorAnalyticsFallback from '@/components/layouts/ErrorFallbacks/ErrorAnalyticsFallback';
import clsx from 'clsx';

// Hooks
import useAcl from '@/utils/hooks/user/useAcl';

//lib
import { aclFeatures } from '@/lib/acl/aclFeatures';
import { ORIENTATION_VERTICAL } from '@/lib/gridElements/orientation';
import { KPI_VALUE_TIME } from '@/utils/hooks/format/useFormatKpiValue';

//components
import KPI, { KPI_STYLES } from '@/components/base/Kpi';
import { DeprecatedKpiBadge } from '@/components/base/Badge/DeprecatedKpiBadge';
import { KpiDataRetention } from '@/components/base/Kpi/KpiDataRetention';

export const KPIDefault = ({
    onReset = () => {
        /** */
    },
    data,
    isLoading,
    config,
    userNumbersFormat,
    children,
    renderLoader = () => {
        /** */
    },
    ...restProps
}) => {
    const { hasAcl } = useAcl();

    return (
        <ErrorBoundary FallbackComponent={ErrorAnalyticsFallback} onReset={() => onReset()}>
            <KPI
                data={data}
                config={config}
                userNumbersFormat={userNumbersFormat}
                isLoading={isLoading}
                kpiStyle={KPI_STYLES.DEFAULT}
                renderLoader={renderLoader}
                {...restProps}>
                <DeprecatedKpiBadge
                    className="-top-2 left-2"
                    deprecated={config.deprecated}
                    network={config.social}
                />
                <KPI.KpiHelpTooltip />
                <KPI.Wrapper
                    className={clsx('px-8 py-12', {
                        'flex-col space-y-8': config.orientation === ORIENTATION_VERTICAL,
                        'flex-col space-x-0 space-y-8 lg:flex-row lg:justify-center lg:space-x-8 lg:space-y-0':
                            config.orientation !== ORIENTATION_VERTICAL
                    })}>
                    <KPI.Icon />
                    <KPI.Content
                        className={clsx({
                            'items-center': config.orientation === ORIENTATION_VERTICAL
                        })}>
                        <KPI.ContentMainDataContainer
                            className={clsx({
                                'lg:items-start': config.orientation !== ORIENTATION_VERTICAL
                            })}>
                            <KPI.MainDataWrapper>
                                <KPI.Promoted />
                                {config?.data_format !== KPI_VALUE_TIME ? (
                                    <KPI.MainDataWithTooltip className="text-3.5xl" />
                                ) : (
                                    <KPI.MainData className="text-3.5xl" />
                                )}
                            </KPI.MainDataWrapper>
                            <KPI.KpiName
                                className={clsx({
                                    'text-center': config.orientation === ORIENTATION_VERTICAL
                                })}
                            />
                        </KPI.ContentMainDataContainer>
                        {hasAcl(aclFeatures.analytic.promoted_post.read) ? (
                            <KPI.SplitOrganicPayed />
                        ) : null}
                        <KPI.DataEvolutionWithTooltip />
                        {children}
                        <KPI.EstimatedNotice />
                        <KpiDataRetention
                            isDisplayed={data?.previous_out_of_bounds}
                            alignText={
                                config.orientation === ORIENTATION_VERTICAL ? 'center' : 'left'
                            }
                        />
                    </KPI.Content>
                </KPI.Wrapper>
            </KPI>
        </ErrorBoundary>
    );
};

KPIDefault.propTypes = {
    onReset: PropTypes.func,
    data: PropTypes.object,
    config: PropTypes.object,
    userNumbersFormat: PropTypes.object,
    isLoading: PropTypes.bool,
    children: PropTypes.node,
    isDashboard: PropTypes.bool
};
